import useAuth from '@nobia/zeus-components/lib/auth/useAuth'
import settings from '@nobia/zeus-components/lib/settings'
import features from '@nobia/zeus-components/lib/features'
import {
  Redirect,
  Route,
  Switch,
  useLocation,
} from '@nobia/zeus-components/lib/router'
import React, { lazy, Suspense, useEffect, useRef } from 'react'

import { useAccountContext } from '../components/AccountContext'
import LoadingPage from '../pages/loading/LoadingPage'
import { gtmPageview, marketoPageview } from '../utils/tracking'
import ProtectedRoute from './ProtectedRoute'
import {
  canNavigateToDesignsPage,
  canNavigateToMessagesPage,
  canNavigateToOrderPage,
  useRoutes,
} from './useRoutes'
import { BadaDevIframed } from '../pages/appointment/components/BadaDevIframed'

const HomePage = lazy(() => import('../pages/home/HomePage'))
const DesignsPage = lazy(() => import('../pages/designs/BrandDesignsPage'))
const MessagesPage = lazy(() => import('../pages/messages/MessagesPage'))
const OrderPage = lazy(() => import('../pages/order/BrandOrderPage'))
const SavedPage = lazy(() => import('../pages/saved/SavedPage'))
const PublicDesignPage = lazy(() =>
  import('../pages/public-designs/PublicDesignPage')
)
const AppointmentPage = lazy(() =>
  import('../pages/appointment/AppointmentPage')
)
const DevPage = lazy(() => import('../pages/dev/DevPage'))
const MultiStorePage = lazy(() => import('../pages/multi-store/MultiStorePage'))

const Routes = () => {
  const initialRedirect = useRef(false)
  const routes = useRoutes()
  const { status, profile } = useAuth()
  const { pathname, hash } = useLocation()
  const { store, stores, setStore, loading } = useAccountContext()
  useEffect(() => {
    gtmPageview(pathname + hash)
  }, [pathname, hash])

  useEffect(() => {
    marketoPageview()
  }, [pathname])

  if (
    features.multiStore === true &&
    status === 'authenticated' &&
    profile.roles.includes('customer') &&
    !initialRedirect.current &&
    !store
  ) {
    if (loading) {
      return <LoadingPage loading navigation={false} />
    }
    initialRedirect.current = true
    if (stores.length > 1) {
      return (
        <Redirect
          to={{
            pathname: routes.multiStore.path,
            state: { returnUrl: location.pathname },
          }}
        />
      )
    }
    if (stores.length === 1) {
      setStore(stores[0].id)
    }
  }

  return (
    <Suspense
      fallback={
        <LoadingPage
          navigation={
            !features.multiStore ||
            (features.multiStore === true &&
              pathname !== routes.multiStore.path)
          }
        />
      }
    >
      <Switch>
        <ProtectedRoute exact path={routes.home.path}>
          <HomePage />
        </ProtectedRoute>
        {canNavigateToDesignsPage(store?.id) ? (
          <ProtectedRoute path={routes.designs.path}>
            <DesignsPage />
          </ProtectedRoute>
        ) : null}
        {canNavigateToMessagesPage(store?.id) ? (
          <ProtectedRoute path={routes.messages.path}>
            <MessagesPage />
          </ProtectedRoute>
        ) : null}
        <ProtectedRoute path={routes.appointment.path}>
          <AppointmentPage />
        </ProtectedRoute>
        {canNavigateToOrderPage(store?.id) ? (
          <ProtectedRoute path={routes.order.path}>
            <OrderPage />
          </ProtectedRoute>
        ) : null}
        <ProtectedRoute path={routes.saved.path}>
          <SavedPage />
        </ProtectedRoute>
        {features.multiStore === true ? (
          <ProtectedRoute path={routes.multiStore.path}>
            <MultiStorePage />
          </ProtectedRoute>
        ) : null}
        <Route path={routes.design.path}>
          <PublicDesignPage />
        </Route>
        {settings.env !== 'prod' && (
          <Route path={settings.bada.embedUrl}>
            <BadaDevIframed />
          </Route>
        )}
        <Route path="/dev">
          <DevPage />
        </Route>
        <Redirect to="/" />
      </Switch>
    </Suspense>
  )
}

export default Routes
